/**
 * JourneyBuilder.jsx - Guest Journey Builder Component
 * 
 * Checklist Items Covered:
 * - I1: Journey Timeline Visual
 * - I2: Journey Step Card
 * - I3: Step Channel Badge
 * - I4: Step Template Link
 * - I5: Step Timing Display
 * - I6: Step Status Badge
 * - I7: Add Step Button
 * - I8: Edit Step Button
 * - I9: Delete Step Button
 * - I10: Reorder Steps
 * - J1: Step Name Input
 * - J2: Channel Selector
 * - J3: Template Selector
 * - J4: Timing Mode Toggle
 * - J5: Fixed DateTime Picker
 * - J6: Offset Value Input
 * - J7: Offset Unit Selector
 * - J8: Save Step Button
 * - J9: Cancel Button
 * 
 * Backend Wiring: Phase 2A COMPLETE
 * - GET /api/journey/steps
 * - POST /api/journey/steps
 * - PUT /api/journey/steps/{id}
 * - DELETE /api/journey/steps/{id}
 * - PUT /api/journey/steps/reorder
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  GitBranch, Mail, MessageSquare, Clock, Calendar, Plus, Edit3, Trash2,
  ChevronUp, ChevronDown, X, Check, Zap, AlertTriangle, Play, Pause,
  Users, CheckCircle2, UserCheck, Search, Hand
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { COLORS } from './messagingStyles';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Types & Constants ──────────────────────────────────────────────────────────

const TIMING_MODES = [
  { key: 'fixed_datetime', label: 'Fixed Date & Time' },
  { key: 'offset', label: 'Relative to Event' },
  { key: 'triggered', label: 'Auto (e.g., after RSVP)' },
  { key: 'manual', label: 'Manual Send Only' },
];

const OFFSET_UNITS = [
  { key: 'minutes', label: 'Minutes' },
  { key: 'hours', label: 'Hours' },
  { key: 'days', label: 'Days' },
  { key: 'weeks', label: 'Weeks' },
];

const STEP_STATUSES = {
  // TRUE STOPLIGHT COLORS
  completed: { label: 'Completed', color: '#16a34a', bg: 'rgba(22,163,74,0.12)' }, // GREEN - complete/sent/confirmed
  active: { label: 'Active', color: '#16a34a', bg: 'rgba(22,163,74,0.12)' }, // GREEN - active/running
  pending: { label: 'Pending', color: '#d97706', bg: 'rgba(217,119,6,0.12)' }, // YELLOW/AMBER - pending/waiting
  paused: { label: 'Paused', color: '#d97706', bg: 'rgba(217,119,6,0.12)' }, // YELLOW/AMBER - in progress/paused
  scheduled: { label: 'Scheduled', color: '#d97706', bg: 'rgba(217,119,6,0.12)' }, // YELLOW - scheduled for future
  failed: { label: 'Failed', color: '#dc2626', bg: 'rgba(220,38,38,0.12)' }, // RED - failed/error
  blocked: { label: 'Blocked', color: '#dc2626', bg: 'rgba(220,38,38,0.12)' }, // RED - blocked/issue
  draft: { label: 'Draft', color: '#6B7280', bg: 'rgba(107,114,128,0.12)' }, // GRAY - not configured yet
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = {
  container: {
    padding: 24,
    minHeight: 'calc(100vh - 200px)',
  },
  
  // Header
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  headerIcon: {
    width: 48,
    height: 48,
    borderRadius: 14,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 13,
    color: COLORS.textMuted,
  },
  
  // I7: Add Step Button
  addStepBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 20px',
    borderRadius: 12,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(198,167,90,0.25)',
    fontSize: 13,
    fontWeight: 700,
    color: '#FFFFFF',
  },
  
  // Backend Notice
  backendNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '14px 20px',
    borderRadius: 12,
    background: 'rgba(245,158,11,0.08)',
    border: '1px solid rgba(245,158,11,0.22)',
    marginBottom: 24,
  },
  backendNoticeText: {
    fontSize: 13,
    color: '#92400e',
    fontWeight: 500,
  },
  
  // I1: Journey Timeline
  timeline: {
    position: 'relative',
    paddingLeft: 40,
  },
  timelineLine: {
    position: 'absolute',
    left: 15,
    top: 24,
    bottom: 24,
    width: 2,
    background: `linear-gradient(180deg, ${COLORS.gold} 0%, ${COLORS.goldLight} 100%)`,
  },
  
  // I2: Journey Step Card
  stepCard: {
    position: 'relative',
    padding: 20,
    borderRadius: 18,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    marginBottom: 16,
    transition: 'all 0.2s',
  },
  stepCardActive: {
    borderColor: COLORS.gold,
    boxShadow: '0 8px 24px rgba(198,167,90,0.15)',
  },
  stepDot: {
    position: 'absolute',
    left: -33,
    top: 24,
    width: 16,
    height: 16,
    borderRadius: 8,
    background: '#d97706', // DEFAULT: Yellow/pending
    border: '3px solid #FFFEFB',
    boxShadow: '0 2px 8px rgba(198,167,90,0.3)',
  },
  stepDotCompleted: {
    background: '#16a34a', // GREEN
  },
  stepDotActive: {
    background: '#16a34a', // GREEN
  },
  stepDotPaused: {
    background: '#d97706', // YELLOW/AMBER
  },
  stepDotPending: {
    background: '#d97706', // YELLOW/AMBER
  },
  stepDotFailed: {
    background: '#dc2626', // RED
  },
  stepDotBlocked: {
    background: '#dc2626', // RED
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  stepNumber: {
    fontSize: 11,
    fontWeight: 700,
    color: COLORS.goldDark,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  stepName: {
    fontSize: 17,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  stepActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  stepActionBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  
  // I3: Channel Badge - Increased font size
  channelBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  channelEmail: {
    background: COLORS.goldLight,
    color: COLORS.goldDark,
  },
  channelSms: {
    background: 'rgba(59,130,246,0.15)',
    color: '#1d4ed8',
  },
  
  // Step Details Grid
  stepDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 16,
  },
  stepDetail: {
    padding: 12,
    borderRadius: 10,
    background: '#FFFCF7',
  },
  stepDetailLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: COLORS.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 6,
  },
  stepDetailValue: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textPrimary,
  },
  
  // I6: Status Badge
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  
  // Empty State
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 60,
    borderRadius: 18,
    border: `1px dashed ${COLORS.borderMedium}`,
    background: 'rgba(255,255,255,0.5)',
    textAlign: 'center',
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 18,
    background: COLORS.goldLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
    marginBottom: 8,
  },
  emptyBody: {
    fontSize: 14,
    color: COLORS.textMuted,
    maxWidth: 360,
    lineHeight: 1.5,
    marginBottom: 24,
  },
  
  // J1-J9: Step Editor Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalCard: {
    width: '100%',
    maxWidth: 560,
    maxHeight: '90vh',
    borderRadius: 20,
    background: COLORS.surface,
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  modalCloseBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  modalBody: {
    flex: 1,
    padding: 24,
    overflowY: 'auto',
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '16px 24px',
    borderTop: `1px solid ${COLORS.borderLight}`,
  },
  
  // Form Fields
  formGroup: {
    marginBottom: 20,
  },
  formLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.textSecondary,
    marginBottom: 8,
  },
  formInput: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    fontSize: 14,
    color: COLORS.textPrimary,
    outline: 'none',
  },
  formSelect: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.inputBg,
    fontSize: 14,
    color: COLORS.textPrimary,
    cursor: 'pointer',
  },
  
  // J2: Channel Selector
  channelSelector: {
    display: 'flex',
    gap: 12,
  },
  channelOption: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '14px 16px',
    borderRadius: 12,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  channelOptionActive: {
    borderColor: COLORS.gold,
    background: COLORS.goldLight,
  },
  channelOptionText: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
  },
  channelOptionTextActive: {
    color: COLORS.goldDark,
  },
  
  // J4: Timing Mode Toggle
  timingModeToggle: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
  },
  timingModeBtn: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textSecondary,
    textAlign: 'center',
  },
  timingModeBtnActive: {
    borderColor: COLORS.gold,
    background: COLORS.goldLight,
    color: COLORS.goldDark,
  },
  
  // Offset Row
  offsetRow: {
    display: 'flex',
    gap: 12,
  },
  offsetInput: {
    flex: 1,
  },
  offsetUnit: {
    flex: 1,
  },
  
  // Buttons
  cancelBtn: {
    padding: '12px 20px',
    borderRadius: 10,
    border: `1px solid ${COLORS.borderMedium}`,
    background: COLORS.surface,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
  },
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 24px',
    borderRadius: 10,
    border: 'none',
    background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    color: '#FFFFFF',
  },
  saveBtnDisabled: {
    background: '#E4DBC9',
    cursor: 'not-allowed',
  },
  
  // ─── Guest Completion Styles ───
  completionBar: {
    marginTop: 16,
    padding: '14px 16px',
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(198,167,90,0.08) 0%, rgba(198,167,90,0.04) 100%)',
    border: '1px solid rgba(198,167,90,0.15)',
  },
  completionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  completionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  completionStats: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  completionStat: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    color: COLORS.textMuted,
  },
  completionStatValue: {
    fontWeight: 700,
    color: COLORS.textPrimary,
  },
  progressBarContainer: {
    height: 8,
    borderRadius: 4,
    background: 'rgba(198,167,90,0.15)',
    overflow: 'hidden',
    marginBottom: 12,
  },
  progressBarFill: {
    height: '100%',
    borderRadius: 4,
    background: `linear-gradient(90deg, ${COLORS.gold} 0%, #D4B96A 100%)`,
    transition: 'width 0.3s ease',
  },
  markCompleteBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 8,
    border: `1px solid ${COLORS.gold}`,
    background: 'rgba(198,167,90,0.08)',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.goldDark,
    transition: 'all 0.15s',
  },
  
  // ─── Guest Selection Modal ───
  guestModalBody: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '70vh',
  },
  guestSearchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 16px',
    borderBottom: `1px solid ${COLORS.borderLight}`,
    background: '#FFFCF7',
  },
  guestSearchInput: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${COLORS.borderMedium}`,
    background: '#FFFFFF',
    fontSize: 14,
    color: COLORS.textPrimary,
    outline: 'none',
  },
  guestListContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
  },
  guestItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 20px',
    cursor: 'pointer',
    transition: 'background 0.15s',
    borderBottom: `1px solid ${COLORS.borderLight}`,
  },
  guestItemSelected: {
    background: 'rgba(198,167,90,0.12)',
  },
  guestItemCompleted: {
    background: 'rgba(22,163,74,0.08)',
  },
  guestCheckbox: {
    width: 20,
    height: 20,
    borderRadius: 5,
    border: `2px solid ${COLORS.borderMedium}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    flexShrink: 0,
  },
  guestCheckboxSelected: {
    background: COLORS.gold,
    borderColor: COLORS.gold,
  },
  guestCheckboxCompleted: {
    background: '#16a34a',
    borderColor: '#16a34a',
  },
  guestInfo: {
    flex: 1,
    minWidth: 0,
  },
  guestName: {
    fontSize: 14,
    fontWeight: 600,
    color: COLORS.textPrimary,
    marginBottom: 2,
  },
  guestMeta: {
    fontSize: 12,
    color: COLORS.textMuted,
  },
  guestCompletedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px',
    borderRadius: 4,
    background: 'rgba(22,163,74,0.12)',
    fontSize: 10,
    fontWeight: 700,
    color: '#16a34a',
    textTransform: 'uppercase',
  },
  guestManualBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    padding: '3px 6px',
    borderRadius: 4,
    background: 'rgba(139,92,246,0.12)',
    fontSize: 9,
    fontWeight: 600,
    color: '#7c3aed',
    marginLeft: 6,
  },
  selectActionsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderTop: `1px solid ${COLORS.borderLight}`,
    background: '#FFFCF7',
  },
  selectCount: {
    fontSize: 13,
    fontWeight: 600,
    color: COLORS.textSecondary,
  },
  selectAllBtn: {
    padding: '6px 12px',
    borderRadius: 6,
    border: `1px solid ${COLORS.borderMedium}`,
    background: '#FFFFFF',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    color: COLORS.textSecondary,
  },
};

// ─── Helper Functions ───────────────────────────────────────────────────────────

const formatTiming = (step) => {
  if (step.timing_mode === 'fixed_datetime' && step.fixed_at) {
    try {
      const date = new Date(step.fixed_at);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid date';
    }
  }
  if (step.offset_value && step.offset_unit) {
    const unit = step.offset_unit === 'days' ? 'day' : step.offset_unit.slice(0, -1);
    return `${step.offset_value} ${step.offset_value === 1 ? unit : step.offset_unit} before event`;
  }
  return 'Not configured';
};

const getTemplateName = (templateId, templates) => {
  const template = templates.find(t => t.id === templateId);
  return template?.name || 'Unknown template';
};

// ─── Component ──────────────────────────────────────────────────────────────────

const JourneyBuilder = ({
  emailTemplates = [],
  smsTemplates = [],
  onBack,
}) => {
  // Journey steps state - fetched from backend
  const [journeySteps, setJourneySteps] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showEditor, setShowEditor] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    channel: 'email',
    template_id: '',
    timing_mode: 'offset',
    fixed_at: '',
    offset_value: '7',
    offset_unit: 'days',
  });

  // ─── Guest Completion State ───
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionStep, setCompletionStep] = useState(null);
  const [allGuests, setAllGuests] = useState([]);
  const [stepCompletions, setStepCompletions] = useState([]);
  const [selectedGuestIds, setSelectedGuestIds] = useState([]);
  const [guestSearch, setGuestSearch] = useState('');
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [savingCompletions, setSavingCompletions] = useState(false);

  // Fetch journey steps from backend on mount
  useEffect(() => {
    const fetchSteps = async () => {
      try {
        const res = await axios.get(`${API}/api/journey/steps`);
        if (res.data && Array.isArray(res.data)) {
          setJourneySteps(res.data);
        }
      } catch (e) {
        console.error('Failed to fetch journey steps:', e);
        toast.error('Failed to load journey steps');
      } finally {
        setLoading(false);
      }
    };
    fetchSteps();
  }, []);

  // Get templates based on selected channel
  const availableTemplates = useMemo(() => {
    return draft.channel === 'sms' ? smsTemplates : emailTemplates;
  }, [draft.channel, emailTemplates, smsTemplates]);

  // Sorted steps by order
  const sortedSteps = useMemo(() => {
    return [...journeySteps].sort((a, b) => a.order - b.order);
  }, [journeySteps]);

  // Open editor for new step
  const handleAddStep = () => {
    setEditingStep(null);
    setDraft({
      name: '',
      channel: 'email',
      template_id: '',
      timing_mode: 'offset',
      fixed_at: '',
      offset_value: '7',
      offset_unit: 'days',
    });
    setShowEditor(true);
  };

  // I8: Edit Step
  const handleEditStep = (step) => {
    setEditingStep(step);
    setDraft({
      name: step.name,
      channel: step.channel,
      template_id: step.template_id || '',
      timing_mode: step.timing_mode || 'offset',
      fixed_at: step.fixed_at || '',
      offset_value: String(step.offset_value || 7),
      offset_unit: step.offset_unit || 'days',
    });
    setShowEditor(true);
  };

  // I9: Delete Step - calls backend API
  const handleDeleteStep = useCallback(async (stepId) => {
    if (!window.confirm('Are you sure you want to delete this journey step?')) return;
    
    try {
      await axios.delete(`${API}/api/journey/steps/${stepId}`);
      setJourneySteps(prev => prev.filter(s => s.id !== stepId));
      toast.success('Journey step deleted');
    } catch (e) {
      console.error('Failed to delete step:', e);
      toast.error('Failed to delete journey step');
    }
  }, []);

  // I10: Reorder Steps - calls backend API
  const handleMoveStep = useCallback(async (stepId, direction) => {
    const steps = [...journeySteps].sort((a, b) => a.order - b.order);
    const index = steps.findIndex(s => (s.id || s._id) === stepId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= steps.length) return;
    
    // Swap positions in array
    const [moved] = steps.splice(index, 1);
    steps.splice(newIndex, 0, moved);
    
    // Assign new order values
    const reorderedSteps = steps.map((s, i) => ({ id: s.id || s._id, order: i + 1 }));
    
    // Optimistically update UI
    setJourneySteps(steps.map((s, i) => ({ ...s, order: i + 1 })));
    
    // Persist to backend
    try {
      await axios.put(`${API}/api/journey/steps/reorder`, { steps: reorderedSteps });
    } catch (e) {
      console.error('Failed to reorder steps:', e);
      toast.error('Failed to reorder steps');
      const res = await axios.get(`${API}/api/journey/steps`);
      if (res.data) setJourneySteps(res.data);
    }
  }, [journeySteps]);

  // J8: Save Step - calls backend API
  const handleSaveStep = async () => {
    if (!draft.name.trim()) {
      toast.error('Please enter a step name');
      return;
    }

    setSaving(true);
    
    const stepData = {
      name: draft.name.trim(),
      channel: draft.channel,
      template_id: draft.template_id || null,
      timing_mode: draft.timing_mode,
      fixed_at: draft.timing_mode === 'fixed_datetime' ? draft.fixed_at : null,
      offset_value: draft.timing_mode === 'offset' ? parseInt(draft.offset_value) || 7 : null,
      offset_unit: draft.timing_mode === 'offset' ? draft.offset_unit : null,
      status: editingStep?.status || 'pending',
    };

    try {
      if (editingStep) {
        // Update existing step
        const res = await axios.put(`${API}/api/journey/steps/${editingStep.id}`, stepData);
        if (res.data?.success && res.data?.step) {
          setJourneySteps(prev => prev.map(s => s.id === editingStep.id ? res.data.step : s));
          toast.success('Journey step updated');
        }
      } else {
        // Create new step
        const res = await axios.post(`${API}/api/journey/steps`, stepData);
        if (res.data?.success && res.data?.step) {
          setJourneySteps(prev => [...prev, res.data.step]);
          toast.success('Journey step created');
        }
      }
      setShowEditor(false);
      setEditingStep(null);
    } catch (e) {
      console.error('Failed to save step:', e);
      toast.error(e.response?.data?.detail || 'Failed to save journey step');
    } finally {
      setSaving(false);
    }
  };

  // J9: Cancel
  const handleCancel = () => {
    setShowEditor(false);
    setEditingStep(null);
  };

  // ─── Guest Completion Handlers ───
  
  // Open the completion modal for a specific step
  const handleOpenCompletionModal = async (step) => {
    setCompletionStep(step);
    setSelectedGuestIds([]);
    setGuestSearch('');
    setShowCompletionModal(true);
    setLoadingGuests(true);
    
    try {
      // Fetch all guests
      const guestsRes = await axios.get(`${API}/api/guests`);
      const guestsData = guestsRes.data?.guests || guestsRes.data || [];
      setAllGuests(guestsData);
      
      // Fetch completions for this step
      const completionsRes = await axios.get(`${API}/api/journey/steps/${step.id}/completions`);
      setStepCompletions(completionsRes.data || []);
    } catch (e) {
      console.error('Failed to load guests/completions:', e);
      toast.error('Failed to load guest data');
    } finally {
      setLoadingGuests(false);
    }
  };

  // Close completion modal
  const handleCloseCompletionModal = () => {
    setShowCompletionModal(false);
    setCompletionStep(null);
    setSelectedGuestIds([]);
    setGuestSearch('');
  };

  // Toggle guest selection
  const handleToggleGuest = (guestId) => {
    setSelectedGuestIds(prev => 
      prev.includes(guestId) 
        ? prev.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  // Select all uncompleted guests
  const handleSelectAllUncompleted = () => {
    const completedGuestIds = new Set(stepCompletions.map(c => c.guest_id));
    const uncompletedIds = allGuests
      .filter(g => !completedGuestIds.has(g.id))
      .map(g => g.id);
    setSelectedGuestIds(uncompletedIds);
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedGuestIds([]);
  };

  // Save completions
  const handleSaveCompletions = async () => {
    if (selectedGuestIds.length === 0 || !completionStep) return;
    
    setSavingCompletions(true);
    try {
      const res = await axios.post(`${API}/api/journey/steps/${completionStep.id}/completions`, {
        guest_ids: selectedGuestIds,
        method: 'manual',
        notes: null
      });
      
      if (res.data?.success) {
        toast.success(res.data.message || `Marked ${res.data.created?.length || 0} guest(s) complete`);
        
        // Refresh the journey steps to update counts
        const stepsRes = await axios.get(`${API}/api/journey/steps`);
        if (stepsRes.data && Array.isArray(stepsRes.data)) {
          setJourneySteps(stepsRes.data);
        }
        
        // Update local completions
        setStepCompletions(prev => [...prev, ...(res.data.created || [])]);
        setSelectedGuestIds([]);
      }
    } catch (e) {
      console.error('Failed to save completions:', e);
      toast.error(e.response?.data?.detail || 'Failed to save completions');
    } finally {
      setSavingCompletions(false);
    }
  };

  // Remove a completion
  const handleRemoveCompletion = async (guestId) => {
    if (!completionStep) return;
    
    try {
      await axios.delete(`${API}/api/journey/steps/${completionStep.id}/completions`, {
        data: { guest_ids: [guestId] }
      });
      
      // Update local state
      setStepCompletions(prev => prev.filter(c => c.guest_id !== guestId));
      
      // Refresh steps to update counts
      const stepsRes = await axios.get(`${API}/api/journey/steps`);
      if (stepsRes.data && Array.isArray(stepsRes.data)) {
        setJourneySteps(stepsRes.data);
      }
      
      toast.success('Completion removed');
    } catch (e) {
      console.error('Failed to remove completion:', e);
      toast.error('Failed to remove completion');
    }
  };

  // Filter guests by search
  const filteredGuests = useMemo(() => {
    if (!guestSearch.trim()) return allGuests;
    const q = guestSearch.toLowerCase();
    return allGuests.filter(g => 
      `${g.first_name || ''} ${g.last_name || ''}`.toLowerCase().includes(q) ||
      (g.email || '').toLowerCase().includes(q) ||
      (g.phone || '').includes(q)
    );
  }, [allGuests, guestSearch]);

  // Check if a guest is completed for current step
  const isGuestCompleted = useCallback((guestId) => {
    return stepCompletions.some(c => c.guest_id === guestId);
  }, [stepCompletions]);

  // Get completion details for a guest
  const getCompletionDetails = useCallback((guestId) => {
    return stepCompletions.find(c => c.guest_id === guestId);
  }, [stepCompletions]);

  const isFormValid = draft.name.trim().length > 0;

  return (
    <div style={styles.container} data-testid="journey-builder">
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIcon}>
            <GitBranch size={24} color={COLORS.gold} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={() => { if (onBack) onBack(); else window.history.back(); }} data-testid="journey-back-btn" style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: COLORS.text, fontFamily: "'DM Sans', system-ui, sans-serif" }}>Back</button>
              <div style={styles.headerTitle}>Guest Journey Builder</div>
            </div>
            <div style={styles.headerSubtitle}>
              Create automated communication sequences for your event
            </div>
          </div>
        </div>
        
        {/* I7: Add Step Button */}
        <button
          style={styles.addStepBtn}
          onClick={handleAddStep}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(198,167,90,0.35)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(198,167,90,0.25)';
          }}
          data-testid="add-step-btn"
        >
          <Plus size={16} />
          Add Journey Step
        </button>
      </div>

      {/* I1: Journey Timeline */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
          Loading journey steps...
        </div>
      ) : sortedSteps.length === 0 ? (
        <div style={styles.emptyState} data-testid="journey-empty">
          <div style={styles.emptyIcon}>
            <GitBranch size={28} color={COLORS.gold} />
          </div>
          <div style={styles.emptyTitle}>No Journey Steps Yet</div>
          <div style={styles.emptyBody}>
            Build an automated communication sequence by adding steps. Each step sends a message at a specific time before your event.
          </div>
          <button
            style={styles.addStepBtn}
            onClick={handleAddStep}
          >
            <Plus size={16} />
            Add Your First Step
          </button>
        </div>
      ) : (
        <div style={styles.timeline} data-testid="journey-timeline">
          <div style={styles.timelineLine} />
          
          {sortedSteps.map((step, index) => {
            const status = STEP_STATUSES[step.status] || STEP_STATUSES.pending;
            const templates = step.channel === 'sms' ? smsTemplates : emailTemplates;
            
            return (
              <div
                key={step.id}
                style={{
                  ...styles.stepCard,
                  ...(step.status === 'active' ? styles.stepCardActive : {}),
                }}
                data-testid={`journey-step-${step.id}`}
              >
                {/* Timeline Dot - TRUE STOPLIGHT COLORS */}
                <div
                  style={{
                    ...styles.stepDot,
                    ...(step.status === 'completed' ? styles.stepDotCompleted : {}),
                    ...(step.status === 'active' ? styles.stepDotActive : {}),
                    ...(step.status === 'paused' ? styles.stepDotPaused : {}),
                    ...(step.status === 'pending' ? styles.stepDotPending : {}),
                    ...(step.status === 'failed' ? styles.stepDotFailed : {}),
                    ...(step.status === 'blocked' ? styles.stepDotBlocked : {}),
                  }}
                />
                
                {/* I2: Step Header */}
                <div style={styles.stepHeader}>
                  <div>
                    <div style={styles.stepNumber}>Step {index + 1}</div>
                    <div style={styles.stepName}>{step.name}</div>
                  </div>
                  
                  {/* Step Actions */}
                  <div style={styles.stepActions}>
                    {/* I10: Reorder */}
                    <button
                      style={styles.stepActionBtn}
                      onClick={() => handleMoveStep(step.id, 'up')}
                      disabled={index === 0}
                      data-testid={`step-up-${step.id}`}
                    >
                      <ChevronUp size={14} color={index === 0 ? '#CCC' : COLORS.textSecondary} />
                    </button>
                    <button
                      style={styles.stepActionBtn}
                      onClick={() => handleMoveStep(step.id, 'down')}
                      disabled={index === sortedSteps.length - 1}
                      data-testid={`step-down-${step.id}`}
                    >
                      <ChevronDown size={14} color={index === sortedSteps.length - 1 ? '#CCC' : COLORS.textSecondary} />
                    </button>
                    
                    {/* I8: Edit */}
                    <button
                      style={styles.stepActionBtn}
                      onClick={() => handleEditStep(step)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = COLORS.goldLight;
                        e.currentTarget.style.borderColor = COLORS.gold;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = COLORS.surface;
                        e.currentTarget.style.borderColor = COLORS.borderMedium;
                      }}
                      data-testid={`step-edit-${step.id}`}
                    >
                      <Edit3 size={14} color={COLORS.goldDark} />
                    </button>
                    
                    {/* I9: Delete */}
                    <button
                      style={styles.stepActionBtn}
                      onClick={() => handleDeleteStep(step.id)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(220,38,38,0.1)';
                        e.currentTarget.style.borderColor = COLORS.error;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = COLORS.surface;
                        e.currentTarget.style.borderColor = COLORS.borderMedium;
                      }}
                      data-testid={`step-delete-${step.id}`}
                    >
                      <Trash2 size={14} color={COLORS.error} />
                    </button>
                  </div>
                </div>
                
                {/* Step Details */}
                <div style={styles.stepDetails}>
                  {/* I3: Channel Badge */}
                  <div style={styles.stepDetail}>
                    <div style={styles.stepDetailLabel}>Channel</div>
                    <span
                      style={{
                        ...styles.channelBadge,
                        ...(step.channel === 'email' ? styles.channelEmail : styles.channelSms),
                      }}
                    >
                      {step.channel === 'email' ? <Mail size={10} /> : <MessageSquare size={10} />}
                      {step.channel.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* I5: Timing Display */}
                  <div style={styles.stepDetail}>
                    <div style={styles.stepDetailLabel}>Timing</div>
                    <div style={styles.stepDetailValue}>
                      <Clock size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} color={COLORS.goldDark} />
                      {formatTiming(step)}
                    </div>
                  </div>
                  
                  {/* I6: Status Badge */}
                  <div style={styles.stepDetail}>
                    <div style={styles.stepDetailLabel}>Status</div>
                    <span
                      style={{
                        ...styles.statusBadge,
                        background: status.bg,
                        color: status.color,
                      }}
                    >
                      {step.status === 'active' && <Play size={10} />}
                      {step.status === 'paused' && <Pause size={10} />}
                      {status.label}
                    </span>
                  </div>
                </div>
                
                {/* I4: Template Link */}
                {step.template_id && (
                  <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: '#FFFCF7' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textMuted, marginBottom: 4 }}>TEMPLATE</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>
                      {getTemplateName(step.template_id, templates)}
                    </div>
                  </div>
                )}
                
                {/* Guest Completion Tracking */}
                <div style={styles.completionBar} data-testid={`step-completion-${step.id}`}>
                  <div style={styles.completionHeader}>
                    <div style={styles.completionTitle}>
                      <Users size={14} color={COLORS.gold} />
                      Guest Completions
                    </div>
                    <div style={styles.completionStats}>
                      <div style={styles.completionStat}>
                        <span style={styles.completionStatValue}>{step.completed_count || 0}</span>
                        <span>/ {step.total_guests || 0}</span>
                      </div>
                      <div style={styles.completionStat}>
                        <span style={{ ...styles.completionStatValue, color: '#7c3aed' }}>{step.manual_count || 0}</span>
                        <Hand size={10} style={{ marginLeft: 2 }} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div style={styles.progressBarContainer}>
                    <div 
                      style={{
                        ...styles.progressBarFill,
                        width: `${step.completion_rate || 0}%`
                      }}
                    />
                  </div>
                  
                  {/* Mark Complete Button */}
                  <button
                    style={styles.markCompleteBtn}
                    onClick={() => handleOpenCompletionModal(step)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(198,167,90,0.18)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(198,167,90,0.08)';
                    }}
                    data-testid={`mark-complete-btn-${step.id}`}
                  >
                    <UserCheck size={14} />
                    Mark Guests Complete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* J1-J9: Step Editor Modal */}
      {showEditor && (
        <div style={styles.modalOverlay} onClick={handleCancel} data-testid="step-editor-modal">
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}>
                {editingStep ? 'Edit Journey Step' : 'Add Journey Step'}
              </span>
              <button style={styles.modalCloseBtn} onClick={handleCancel}>
                <X size={18} color={COLORS.textSecondary} />
              </button>
            </div>
            
            <div style={styles.modalBody}>
              {/* J1: Step Name Input */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Step Name *</label>
                <input
                  type="text"
                  value={draft.name}
                  onChange={(e) => setDraft(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Save the Date Notification"
                  style={styles.formInput}
                  data-testid="step-name-input"
                />
              </div>

              {/* Step Code — read-only after creation, shows stable internal key */}
              {editingStep?.step_code ? (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ ...styles.formLabel, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Internal Code
                    <span style={{ fontSize: 10, fontWeight: 400, color: '#9B7420', fontStyle: 'italic' }}>
                      (stable — survives renames)
                    </span>
                  </label>
                  <div style={{ padding: '8px 12px', borderRadius: 8,
                    background: 'rgba(198,167,90,0.07)', border: '1px solid rgba(198,167,90,0.22)',
                    fontFamily: 'monospace', fontSize: 12, color: '#5A4E38', letterSpacing: '0.3px' }}>
                    {editingStep.step_code}
                  </div>
                </div>
              ) : null}
              
              {/* J2: Channel Selector */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Channel</label>
                <div style={styles.channelSelector}>
                  <div
                    style={{
                      ...styles.channelOption,
                      ...(draft.channel === 'email' ? styles.channelOptionActive : {}),
                    }}
                    onClick={() => setDraft(prev => ({ ...prev, channel: 'email', template_id: '' }))}
                    data-testid="channel-email"
                  >
                    <Mail size={16} color={draft.channel === 'email' ? COLORS.goldDark : COLORS.textMuted} />
                    <span style={{
                      ...styles.channelOptionText,
                      ...(draft.channel === 'email' ? styles.channelOptionTextActive : {}),
                    }}>Email</span>
                  </div>
                  <div
                    style={{
                      ...styles.channelOption,
                      ...(draft.channel === 'sms' ? styles.channelOptionActive : {}),
                    }}
                    onClick={() => setDraft(prev => ({ ...prev, channel: 'sms', template_id: '' }))}
                    data-testid="channel-sms"
                  >
                    <MessageSquare size={16} color={draft.channel === 'sms' ? COLORS.goldDark : COLORS.textMuted} />
                    <span style={{
                      ...styles.channelOptionText,
                      ...(draft.channel === 'sms' ? styles.channelOptionTextActive : {}),
                    }}>SMS</span>
                  </div>
                </div>
              </div>
              
              {/* J3: Template Selector */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Template (Optional)</label>
                {availableTemplates.length === 0 ? (
                  <div style={{
                    padding: '12px 14px',
                    background: 'rgba(198,167,90,0.08)',
                    border: '1px solid rgba(198,167,90,0.2)',
                    borderRadius: 8,
                    fontSize: 13,
                    color: '#6B5E4A',
                  }}>
                    No templates available for {draft.channel === 'sms' ? 'SMS' : 'Email'}.
                    <br/>
                    <span style={{ fontSize: 12, color: '#8B8478' }}>
                      Create templates in the Templates tab above.
                    </span>
                  </div>
                ) : (
                  <select
                    value={draft.template_id}
                    onChange={(e) => setDraft(prev => ({ ...prev, template_id: e.target.value }))}
                    style={styles.formSelect}
                    data-testid="template-selector"
                  >
                    <option value="">Select a template...</option>
                    {availableTemplates.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name} {t.is_live ? '(Live)' : ''} {t.is_active ? '(Active)' : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              {/* J4: Timing Mode Toggle */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Timing</label>
                <div style={styles.timingModeToggle}>
                  {TIMING_MODES.map(mode => (
                    <button
                      key={mode.key}
                      style={{
                        ...styles.timingModeBtn,
                        ...(draft.timing_mode === mode.key ? styles.timingModeBtnActive : {}),
                      }}
                      onClick={() => setDraft(prev => ({ ...prev, timing_mode: mode.key }))}
                      type="button"
                      data-testid={`timing-${mode.key}`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
                
                {/* J5: Fixed DateTime Picker */}
                {draft.timing_mode === 'fixed_datetime' && (
                  <input
                    type="datetime-local"
                    value={draft.fixed_at}
                    onChange={(e) => setDraft(prev => ({ ...prev, fixed_at: e.target.value }))}
                    style={styles.formInput}
                    data-testid="fixed-datetime"
                  />
                )}
                
                {/* J6-J7: Offset Value & Unit */}
                {draft.timing_mode === 'offset' && (
                  <div style={styles.offsetRow}>
                    <div style={styles.offsetInput}>
                      <input
                        type="number"
                        value={draft.offset_value}
                        onChange={(e) => setDraft(prev => ({ ...prev, offset_value: e.target.value }))}
                        min="1"
                        style={styles.formInput}
                        data-testid="offset-value"
                      />
                    </div>
                    <div style={styles.offsetUnit}>
                      <select
                        value={draft.offset_unit}
                        onChange={(e) => setDraft(prev => ({ ...prev, offset_unit: e.target.value }))}
                        style={styles.formSelect}
                        data-testid="offset-unit"
                      >
                        {OFFSET_UNITS.map(unit => (
                          <option key={unit.key} value={unit.key}>{unit.label} before event</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div style={styles.modalFooter}>
              {/* J9: Cancel Button */}
              <button style={styles.cancelBtn} onClick={handleCancel} data-testid="cancel-btn">
                Cancel
              </button>
              
              {/* J8: Save Step Button */}
              <button
                style={{
                  ...styles.saveBtn,
                  ...((!isFormValid || saving) ? styles.saveBtnDisabled : {}),
                }}
                onClick={handleSaveStep}
                disabled={!isFormValid || saving}
                data-testid="save-step-btn"
              >
                <Check size={16} />
                {saving ? 'Saving...' : (editingStep ? 'Update Step' : 'Add Step')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guest Completion Modal */}
      {showCompletionModal && completionStep && (
        <div style={styles.modalOverlay} onClick={handleCloseCompletionModal} data-testid="completion-modal">
          <div 
            style={{ ...styles.modalCard, maxWidth: 640, maxHeight: '85vh' }} 
            onClick={e => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <span style={styles.modalTitle}>Mark Guests Complete</span>
                <div style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 4 }}>
                  {completionStep.name}
                </div>
              </div>
              <button style={styles.modalCloseBtn} onClick={handleCloseCompletionModal}>
                <X size={18} color={COLORS.textSecondary} />
              </button>
            </div>
            
            <div style={styles.guestModalBody}>
              {/* Search Bar */}
              <div style={styles.guestSearchBar}>
                <Search size={18} color={COLORS.textMuted} />
                <input
                  type="text"
                  value={guestSearch}
                  onChange={(e) => setGuestSearch(e.target.value)}
                  placeholder="Search guests by name, email, or phone..."
                  style={styles.guestSearchInput}
                  data-testid="guest-search"
                />
              </div>
              
              {/* Selection Actions */}
              <div style={styles.selectActionsBar}>
                <span style={styles.selectCount}>
                  {selectedGuestIds.length} selected
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button 
                    style={styles.selectAllBtn}
                    onClick={handleSelectAllUncompleted}
                    data-testid="select-all-btn"
                  >
                    Select All Uncompleted
                  </button>
                  {selectedGuestIds.length > 0 && (
                    <button 
                      style={styles.selectAllBtn}
                      onClick={handleClearSelection}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              
              {/* Guest List */}
              <div style={styles.guestListContainer}>
                {loadingGuests ? (
                  <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
                    Loading guests...
                  </div>
                ) : filteredGuests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
                    {guestSearch ? 'No guests match your search' : 'No guests found'}
                  </div>
                ) : (
                  filteredGuests.map(guest => {
                    const completed = isGuestCompleted(guest.id);
                    const completionDetails = getCompletionDetails(guest.id);
                    const isSelected = selectedGuestIds.includes(guest.id);
                    
                    return (
                      <div
                        key={guest.id}
                        style={{
                          ...styles.guestItem,
                          ...(completed ? styles.guestItemCompleted : {}),
                          ...(isSelected && !completed ? styles.guestItemSelected : {}),
                        }}
                        onClick={() => !completed && handleToggleGuest(guest.id)}
                        data-testid={`guest-item-${guest.id}`}
                      >
                        {/* Checkbox or Completed Indicator */}
                        <div
                          style={{
                            ...styles.guestCheckbox,
                            ...(completed ? styles.guestCheckboxCompleted : {}),
                            ...(isSelected && !completed ? styles.guestCheckboxSelected : {}),
                          }}
                        >
                          {(completed || isSelected) && (
                            <Check size={12} color="#FFFFFF" />
                          )}
                        </div>
                        
                        {/* Guest Info */}
                        <div style={styles.guestInfo}>
                          <div style={styles.guestName}>
                            {guest.first_name || ''} {guest.last_name || ''}
                            {!guest.first_name && !guest.last_name && <em>No name</em>}
                          </div>
                          <div style={styles.guestMeta}>
                            {guest.email || guest.phone || 'No contact info'}
                          </div>
                        </div>
                        
                        {/* Completion Status */}
                        {completed && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={styles.guestCompletedBadge}>
                              <CheckCircle2 size={10} />
                              Complete
                            </span>
                            {completionDetails?.method === 'manual' && (
                              <span style={styles.guestManualBadge}>
                                <Hand size={8} />
                                Manual
                              </span>
                            )}
                            {/* Remove button */}
                            <button
                              style={{
                                padding: '4px 8px',
                                borderRadius: 4,
                                border: '1px solid rgba(220,38,38,0.3)',
                                background: 'rgba(220,38,38,0.08)',
                                cursor: 'pointer',
                                fontSize: 10,
                                color: '#dc2626',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveCompletion(guest.id);
                              }}
                              data-testid={`remove-completion-${guest.id}`}
                            >
                              Remove
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={handleCloseCompletionModal}>
                Close
              </button>
              <button
                style={{
                  ...styles.saveBtn,
                  ...(selectedGuestIds.length === 0 || savingCompletions ? styles.saveBtnDisabled : {}),
                }}
                onClick={handleSaveCompletions}
                disabled={selectedGuestIds.length === 0 || savingCompletions}
                data-testid="save-completions-btn"
              >
                <CheckCircle2 size={16} />
                {savingCompletions 
                  ? 'Saving...' 
                  : `Mark ${selectedGuestIds.length} Guest${selectedGuestIds.length !== 1 ? 's' : ''} Complete`
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JourneyBuilder;
